import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  contentChild,
  ElementRef,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatSidenavModule } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { ColorSchemeService } from '@examdojo/browser/color-scheme';
import { IconComponent } from '@examdojo/core/icon';
import { AppLogoComponent } from '@examdojo/ui/app-logo';
import { distinctUntilDeepChanged, fromResize } from '@examdojo/core/rxjs';
import { PlatformService } from '@examdojo/platform';
import { UserQuery } from '@examdojo/user';
import { TranslocoPipe } from '@jsverse/transloco';
import { combineLatest, distinctUntilChanged, filter, map, shareReplay, startWith, switchMap, tap } from 'rxjs';
import { injectNavPath } from './nav-segment';
import { SidebarProfileLinkComponent } from './sidebar-profile-link/sidebar-profile-link.component';
import { isSidenavEntryActive, SidenavEntry, SidenavItem, SidenavSection } from './sidenav.model';

interface SidenavRouteData {
  compactView: boolean;
  minimizedView: boolean;
  hideSidenav: boolean;
}

const NO_SIDENAV_ROUTE_DATA: SidenavRouteData = { compactView: false, minimizedView: false, hideSidenav: false };

function getSidenavRouteData(router: Router): SidenavRouteData {
  const shellRoute = router.routerState.root.firstChild;

  if (!shellRoute) {
    return NO_SIDENAV_ROUTE_DATA;
  }

  let current = shellRoute.snapshot.firstChild;
  const data: Partial<SidenavRouteData> = {};

  while (current) {
    for (const key of Object.keys(NO_SIDENAV_ROUTE_DATA) as Array<keyof SidenavRouteData>) {
      if (current.data?.[key] !== undefined) {
        data[key] = current.data[key];
      }
    }
    current = current.firstChild;
  }

  return { ...NO_SIDENAV_ROUTE_DATA, ...data };
}

/**
 * How the rail is drawn. `classic` is the teacher and examdojo rail; `rail` is
 * the current navigation design: grey until selected, no tint behind the
 * selected row, and three widths (default, collapsed, and a minimized card a
 * route asks for with `data: { minimizedView: true }`).
 */
export type SidenavAppearance = 'classic' | 'rail';

@Component({
  selector: 'dojo-sidenav',
  imports: [
    NgTemplateOutlet,
    RouterLink,
    TranslocoPipe,
    RouterLinkActive,
    MatSidenavModule,
    IconComponent,
    NgClass,
    SidebarProfileLinkComponent,
    AppLogoComponent,
  ],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'h-full flex flex-col',
    '[class.sidenav-hidden]': 'state.isHidden',
    '[class.sidenav--rail]': "appearance() === 'rail'",
  },
})
export class SidenavComponent {
  constructor(
    private readonly userQuery: UserQuery,
    private readonly router: Router,
    private readonly colorSchemeService: ColorSchemeService,
    private readonly platformService: PlatformService,
  ) {
    this.observeMobileTopPanelHeight()
      .pipe(
        takeUntilDestroyed(),
        tap((height) => this.mobileTopPanelHeight.set(height)),
      )
      .subscribe();
  }

  readonly NORMAL_WIDTH = 240;
  readonly MINIMIZED_WIDTH = 72;

  readonly items = input.required<SidenavItem[]>();
  /** Groups of rows drawn from data under the items, on the desktop rail only. */
  readonly sections = input<readonly SidenavSection[]>([]);
  readonly appearance = input<SidenavAppearance>('classic');
  readonly compactWidth = input(80);
  readonly logoCompact = input.required<string>();
  readonly logoCompactDark = input<string>();
  /** Sized for the sensei mark; a letter mark is drawn far narrower. */
  readonly logoCompactClass = input('w-[44px]');
  readonly logoFullClass = input('w-[111px]');
  readonly logoFull = input.required<string>();
  readonly logoFullDark = input<string>();
  readonly logoAlt = input<string>('Logo');
  readonly settingsRoute = input.required<string>();
  readonly hasMobileTopPanel = input(false);
  readonly hasInfoBanner = input(false);
  readonly showMobileMoreButton = input(true);
  readonly isMoreMenuOpen = input<boolean>(false);

  readonly moreClicked = output<void>();
  readonly menuItemClicked = output<void>();

  readonly customFooter = contentChild<TemplateRef<unknown>>('sidenavFooter');
  readonly mobileTopPanel = contentChild<TemplateRef<unknown>>('mobileTopPanel');
  readonly mobileTopPanelContainer = viewChild<ElementRef<HTMLElement>>('mobileTopPanelContainer');
  readonly customLogo = contentChild<TemplateRef<unknown>>('customLogo');
  /**
   * Drawn at the desktop rail's foot in place of the signed-in user's link, with `user` and
   * `isCompact` in its context.
   */
  readonly profile = contentChild<TemplateRef<unknown>>('sidenavProfile');

  private readonly mobileTopPanelHeight = signal(0);

  readonly isMobile$ = this.platformService.isSmall$.pipe(distinctUntilChanged());

  private readonly sidenavRouteData$ = this.router.events.pipe(
    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    // Read at once too: a rail created inside the navigation that activates it misses its end.
    startWith(null),
    map(() => getSidenavRouteData(this.router)),
    distinctUntilDeepChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly isCompact$ = combineLatest([
    this.platformService.resize$.pipe(
      startWith(null),
      map(() => this.platformService.width < 1100),
      distinctUntilChanged(),
    ),
    this.sidenavRouteData$.pipe(
      map((data) => data.compactView),
      distinctUntilChanged(),
    ),
    this.isMobile$,
  ]).pipe(
    map(([isScreenSmall, isCompactView, isMobile]) => {
      if (isMobile) {
        return false;
      }
      return isScreenSmall || isCompactView;
    }),
  );

  readonly isHidden$ = this.sidenavRouteData$.pipe(
    map((data) => data.hideSidenav),
    distinctUntilChanged(),
  );

  readonly isMinimized$ = combineLatest([this.sidenavRouteData$, this.isMobile$]).pipe(
    map(([data, isMobile]) => data.minimizedView && !isMobile),
    distinctUntilChanged(),
  );

  readonly width$ = combineLatest([
    this.isHidden$,
    this.isMinimized$,
    this.isCompact$,
    toObservable(this.compactWidth),
  ]).pipe(
    map(([isHidden, isMinimized, isCompact, compactWidth]) => {
      if (isHidden) {
        return 0;
      }
      if (isMinimized) {
        return this.MINIMIZED_WIDTH;
      }
      return isCompact ? compactWidth : this.NORMAL_WIDTH;
    }),
  );

  private readonly path = injectNavPath();

  readonly state = connectState({
    user: this.userQuery.active$,
    darkMode: this.colorSchemeService.hasDarkMode$,
    isMobile: this.isMobile$,
    isCompact: this.isCompact$,
    isHidden: this.isHidden$,
    isMinimized: this.isMinimized$,
    width: this.width$,
    mobileTopPanelHeight: toObservable(this.mobileTopPanelHeight),
  });

  isEntryActive(entry: SidenavEntry): boolean {
    return isSidenavEntryActive(entry, this.path());
  }

  navigateToProfile() {
    this.router.navigate([this.settingsRoute()]);
  }

  onMenuItemClick() {
    this.menuItemClicked.emit();
  }

  onMoreClick() {
    this.moreClicked.emit();
  }

  private observeMobileTopPanelHeight() {
    return toObservable(this.mobileTopPanelContainer).pipe(
      filter(Boolean),
      map((container) => container.nativeElement),
      switchMap((element) => fromResize(element, { emitOnStart: true })),
      map((rect) => rect.height),
    );
  }
}
